import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { TopBar } from '@/components/layout/TopBar';
import { ConstructionCanvas } from '@/components/canvas/ConstructionCanvas';
import { MeasurementForm } from '@/components/forms/MeasurementForm';
import axios from 'axios';
import { toast } from 'sonner';
import { RotateCcw, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { offlineDb } from '@/offline/db';
import { syncManager, shouldQueue, postWithTimeout } from '@/offline/syncManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Id generado en cliente: permite guardar sin conexión y sincronizar sin duplicados
const genId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      });

/**
 * Normalize unit string to canonical form.
 * CRITICAL: "m" alone = metro lineal (ml), NOT metro cuadrado
 */
const normalizeUnit = (unitStr) => {
  if (!unitStr) return 'm²';
  const unit = unitStr.trim().toLowerCase();
  if (['m3-km', 'm3km', 'm³-km', 'm³km'].includes(unit)) return 'm3-km';
  if (['m²', 'm2', 'metros cuadrados', 'metro cuadrado', 'mts2', 'mt2'].includes(unit)) return 'm²';
  if (['m³', 'm3', 'metros cubicos', 'metros cúbicos', 'mts3', 'mt3'].includes(unit)) return 'm³';
  if (['ml', 'm', 'metro', 'metros', 'metro lineal', 'metros lineales', 'mt', 'mts'].includes(unit)) return 'ml';
  if (['pza', 'pz', 'pieza', 'piezas', 'pzas', 'unidad', 'unidades', 'und', 'u'].includes(unit)) return 'pza';
  if (['kg', 'kilogramo', 'kilogramos', 'kilo', 'kilos'].includes(unit)) return 'kg';
  if (['ton', 'tonelada', 'toneladas', 'tn', 't'].includes(unit)) return 'ton';
  if (['litro', 'litros', 'lt', 'lto', 'lts', 'l'].includes(unit)) return 'lt';
  return unitStr;
};

// State Machine - Sequential workflow states
const CAPTURE_STATES = {
  NO_PHOTO: 'NO_PHOTO',
  PHOTO_LOADED: 'PHOTO_LOADED',
  SHAPE_SELECTED: 'SHAPE_SELECTED',
  SHAPE_CONFIRMED: 'SHAPE_CONFIRMED',
  PHOTO_COMPLETE: 'PHOTO_COMPLETE',
};

export const CapturePage = () => {
  const navigate = useNavigate();
  const { selectedProject, selectedConcept, setSelectedConcept, captureDestino } = useApp();
  const { user } = useAuth();
  
  // Core state machine
  const [captureState, setCaptureState] = useState(CAPTURE_STATES.NO_PHOTO);
  
  // Photo and capture data
  const [photoBase64, setPhotoBase64] = useState(null);
  const [captureId, setCaptureId] = useState(null);
  const [photoNumber, setPhotoNumber] = useState(null);  // Photo consecutive number
  // Captura actual: { id, isLocal } — isLocal = guardada en el dispositivo, pendiente de sincronizar
  const captureRef = useRef(null);
  const capturePromiseRef = useRef(null); // promesa en vuelo para evitar capturas duplicadas
  
  // Shape data
  const [currentShape, setCurrentShape] = useState(null);
  const [savedShapes, setSavedShapes] = useState([]);
  const [isDeduction, setIsDeduction] = useState(false);
  const [selectedShapeType, setSelectedShapeType] = useState(null);
  const [isDeductionLocked, setIsDeductionLocked] = useState(false);
  
  // Figures data - loaded from database (global per project, never resets)
  const [figures, setFigures] = useState([]);
  const [figureCounter, setFigureCounter] = useState(1);
  const [figureCounterLoaded, setFigureCounterLoaded] = useState(false);
  
  // Live measurements for dimension labels
  const [liveMeasurements, setLiveMeasurements] = useState({});
  
  // Pins for pza mode
  const [pins, setPins] = useState([]);
  
  // Intelligence analysis ID from CONFIRMAR FIGURA
  const [intelligenceAnalysisId, setIntelligenceAnalysisId] = useState(null);
  
  // GenerIA Calibration Card detection status
  const [calibrationStatus, setCalibrationStatus] = useState(null);
  // { regla_detected: bool, regla_config: 'A'|'B'|null, cards_count: 0-10, reference_mm: 0-1000 }
  
  // Silent figure prediction ID (Vision vs Residente)
  const [figurePredictionId, setFigurePredictionId] = useState(null);
  
  // Measurement prediction ID (Vision vs Residente - Mediciones)
  const [measurementPredictionId, setMeasurementPredictionId] = useState(null);
  
  // Orientation detection
  const [isLandscape, setIsLandscape] = useState(true);
  
  const canvasRef = useRef(null);

  // Load next photo number on mount (for display before photo is saved)
  // Sin conexión: usa el último número conocido + capturas locales pendientes
  const loadNextPhotoNumber = useCallback(async () => {
    if (!selectedProject) return;
    const key = `generia_next_photo_${selectedProject.id}`;
    let base = parseInt(localStorage.getItem(key) || '1', 10);
    try {
      const response = await axios.get(`${API}/captures/next-number/${selectedProject.id}`, { timeout: 8000 });
      base = response.data.next_number;
      localStorage.setItem(key, String(base));
    } catch (error) {
      console.warn('Offline: usando numeración local de fotos');
    }
    try {
      const pending = await offlineDb.countPendingForProject(selectedProject.id);
      setPhotoNumber(base + pending.captures);
    } catch {
      setPhotoNumber(base);
    }
  }, [selectedProject]);

  useEffect(() => {
    loadNextPhotoNumber();
  }, [loadNextPhotoNumber]);

  // Load the next figure number from database on mount (global per project)
  useEffect(() => {
    const loadNextFigureNumber = async () => {
      if (!selectedProject || figureCounterLoaded) return;
      const key = `generia_next_figure_${selectedProject.id}`;
      let base = parseInt(localStorage.getItem(key) || '1', 10);
      try {
        const response = await axios.get(`${API}/figures/next-number/${selectedProject.id}`, { timeout: 8000 });
        base = response.data.next_number;
        localStorage.setItem(key, String(base));
      } catch (error) {
        console.warn('Offline: usando numeración local de figuras');
      }
      try {
        const pending = await offlineDb.countPendingForProject(selectedProject.id);
        setFigureCounter(base + pending.figures);
      } catch {
        setFigureCounter(base);
      }
      setFigureCounterLoaded(true);
    };
    loadNextFigureNumber();
  }, [selectedProject, figureCounterLoaded]);

  // Detect orientation changes
  useEffect(() => {
    const checkOrientation = () => {
      const isLandscapeMode = window.innerWidth > window.innerHeight;
      setIsLandscape(isLandscapeMode);
    };

    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  // STATE TRANSITIONS
  
  // 1. Photo uploaded -> PHOTO_LOADED (local state only)
  // Note: figureCounter is NOT reset - it continues from last project number in DB
  const handlePhotoUpload = async (base64) => {
    setPhotoBase64(base64);
    setSavedShapes([]);
    setCurrentShape(null);
    setFigures([]);
    setCaptureId(null);
    captureRef.current = null;
    capturePromiseRef.current = null;
    setSelectedShapeType(null);
    setLiveMeasurements({});
    setPins([]);
    setIsDeduction(false);
    setIsDeductionLocked(false);
    setFigurePredictionId(null);  // Reset prediction for new photo
    setCaptureState(CAPTURE_STATES.PHOTO_LOADED);
    toast.success('Foto cargada');
    
    // Silent figure prediction (non-blocking, never shown to user). Se omite sin conexión.
    try {
      if (selectedConcept && selectedProject && navigator.onLine) {
        const response = await postWithTimeout(`${API}/intelligence/predict-figure`, {
          photo_base64: base64,
          concept_name: selectedConcept.clave || selectedConcept.descripcion,
          concept_description: selectedConcept.descripcion || '',
          unit: selectedConcept.unidad,
          project_id: selectedProject.id,
          concept_id: selectedConcept.id
        });
        if (response.data?.prediction_id) {
          setFigurePredictionId(response.data.prediction_id);
          console.log('FigurePrediction: Started silent analysis', response.data.prediction_id);
        }
      }
    } catch (error) {
      // Silent failure - never interrupt residente flow
      console.error('FigurePrediction: Error starting prediction:', error.message);
    }
  };
  
  // Helper: Create capture (called only when adding first figure).
  // Regresa { id, isLocal }. Sin conexión (o si la red falla) se guarda en IndexedDB.
  // Cachea la promesa en vuelo para evitar crear capturas duplicadas si se llama en paralelo
  // (p.ej. 'Confirmar figura' aún esperando /api/captures cuando el usuario toca 'Agregar figura').
  const createCaptureIfNeeded = () => {
    if (captureRef.current) return Promise.resolve(captureRef.current);
    if (capturePromiseRef.current) return capturePromiseRef.current;

    const run = async () => {
      const id = genId();
      const created_at = new Date().toISOString();
      const payload = {
        id,
        created_at,
        project_id: selectedProject.id,
        concept_id: selectedConcept.id,
        inspector_id: user.id,
        inspector_name: user.name,
        photo_base64: photoBase64,
        canvas_json: '',
        destino: captureDestino || null,
      };

      if (navigator.onLine) {
        try {
          const response = await postWithTimeout(`${API}/captures`, payload);
          captureRef.current = { id: response.data.id, isLocal: false };
          setCaptureId(response.data.id);
          // Update photo number from response (assigned by backend)
          if (response.data.photo_number) {
            setPhotoNumber(response.data.photo_number);
          }
          return captureRef.current;
        } catch (error) {
          if (!shouldQueue(error)) {
            console.error('Error creating capture:', error);
            throw error;
          }
          console.warn('Offline: red no disponible, guardando captura en el dispositivo');
        }
      }

      await offlineDb.putCapture({ id, project_id: selectedProject.id, payload, created_at, synced: false });
      syncManager.refreshPending();
      captureRef.current = { id, isLocal: true };
      setCaptureId(id);
      return captureRef.current;
    };

    const promise = run().finally(() => { capturePromiseRef.current = null; });
    capturePromiseRef.current = promise;
    return promise;
  };

  // Helper: Save figure. Online → backend; sin conexión o captura local → IndexedDB.
  // Regresa { data, local }
  const saveFigure = async (cap, figureData, figureNumber) => {
    const id = genId();
    const created_at = new Date().toISOString();
    const payload = { ...figureData, id, created_at };

    if (!cap.isLocal && navigator.onLine) {
      try {
        const response = await postWithTimeout(`${API}/figures`, payload);
        return { data: response.data, local: false };
      } catch (error) {
        if (!shouldQueue(error)) throw error;
        console.warn('Offline: red no disponible, guardando figura en el dispositivo');
      }
    }

    await offlineDb.putFigure({ id, capture_id: cap.id, project_id: selectedProject.id, payload, created_at });
    syncManager.refreshPending();
    return { data: { ...payload, figure_number: figureNumber, local: true }, local: true };
  };

  // 2. Shape type selected -> SHAPE_SELECTED
  // Lock SUMA/DEDUCCIÓN once shape is placed
  const handleShapeSelect = (shapeType) => {
    setSelectedShapeType(shapeType);
    setLiveMeasurements({});
    setIsDeductionLocked(true); // Lock the toggle
    setCaptureState(CAPTURE_STATES.SHAPE_SELECTED);
  };

  // Poll for GenerIA calibration card detection status
  const pollCalibrationStatus = async (analysisId) => {
    const maxAttempts = 10;
    const interval = 1500; // 1.5 seconds
    
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        const response = await axios.get(`${API}/intelligence/status/${analysisId}`);
        const data = response.data;
        
        if (data.status === 'vision_completed') {
          // Vision analysis complete - update calibration status
          setCalibrationStatus({
            regla_detected: data.regla_detected || false,
            regla_config: data.regla_config || null,
            cards_count: data.cards_count || 0,
            reference_mm: data.reference_mm || 0
          });
          console.log('Intelligence: Calibration status updated:', data);
          return;
        } else if (data.status === 'error') {
          console.log('Intelligence: Analysis error:', data.error_message);
          setCalibrationStatus({ regla_detected: false, regla_config: null, cards_count: 0, reference_mm: 0 });
          return;
        }
        
        // Still analyzing, wait and retry
        await new Promise(resolve => setTimeout(resolve, interval));
      } catch (error) {
        console.error('Intelligence: Polling error:', error.message);
      }
    }
    
    // Max attempts reached, assume no card detected
    console.log('Intelligence: Polling timeout, no card detected');
    setCalibrationStatus({ regla_detected: false, regla_config: null, cards_count: 0, reference_mm: 0 });
  };

  // 3. Shape confirmed -> SHAPE_CONFIRMED
  // INTELLIGENCE: Trigger Vision analysis in background (before measurements are visible)
  const handleConfirmShape = async () => {
    console.log('=== CONFIRMAR FIGURA triggered ===');
    console.log('currentShape:', currentShape ? 'exists' : 'null');
    console.log('photoBase64:', photoBase64 ? `exists (${photoBase64.length} chars)` : 'null');
    console.log('selectedConcept:', selectedConcept ? selectedConcept.id : 'null');
    console.log('captureId (before):', captureId);
    
    if (currentShape) {
      setCaptureState(CAPTURE_STATES.SHAPE_CONFIRMED);
      toast.success('Figura confirmada. Ingresa las medidas.');
      
      // Trigger Intelligence analysis in background (silent, non-blocking)
      // This happens BEFORE measurements are entered = no cheating
      // Sin conexión se omite por completo (la captura se crea al agregar la figura)
      try {
        if (photoBase64 && selectedConcept && selectedProject && navigator.onLine) {
          console.log('Intelligence: Conditions met, creating capture if needed...');
          
          // Create capture first if it doesn't exist
          const cap = await createCaptureIfNeeded();
          const currentCaptureId = cap && !cap.isLocal ? cap.id : null;
          console.log('Intelligence: Capture:', currentCaptureId, cap?.isLocal ? '(local, se omite Vision)' : '');
          
          if (currentCaptureId) {
            console.log('Intelligence: Sending to /api/intelligence/analyze-shape');
            const response = await postWithTimeout(`${API}/intelligence/analyze-shape`, {
              capture_id: currentCaptureId,
              project_id: selectedProject.id,
              concept_id: selectedConcept.id,
              figura_type: currentShape.type || 'rectangle',
              shape_data: currentShape,
              photo_base64: photoBase64
            });
            
            console.log('Intelligence: API response:', response.data);
            
            if (response.data?.analysis_id) {
              setIntelligenceAnalysisId(response.data.analysis_id);
              console.log('Intelligence: Analysis ID set:', response.data.analysis_id);
              
              // Start polling for calibration status (non-blocking)
              pollCalibrationStatus(response.data.analysis_id);
            } else {
              console.log('Intelligence: No analysis_id in response');
            }
            
            // Trigger Measurement Prediction (Vision vs Residente - Mediciones)
            // This predicts largo/alto BEFORE residente enters measurements
            try {
              const shapeAsPercent = canvasRef.current?.getShapeAsPercent?.(currentShape) || currentShape;
              const puntos = (shapeAsPercent.vertices || []).map(v => ({
                x: v.x / 100,
                y: v.y / 100
              }));
              
              const measurementResponse = await postWithTimeout(`${API}/intelligence/analyze-measurements`, {
                capture_id: currentCaptureId,
                contract_id: selectedProject.id,
                concept_id: selectedConcept.id,
                concept_name: selectedConcept.descripcion || selectedConcept.clave,
                unit: selectedConcept.unidad,
                figura_type: currentShape.type || 'rectangle',
                puntos: puntos,
                photo_base64: photoBase64,
                photo_number: photoNumber
              });
              
              if (measurementResponse.data?.prediction_id) {
                setMeasurementPredictionId(measurementResponse.data.prediction_id);
                console.log('MeasurementPrediction: ID set:', measurementResponse.data.prediction_id);
              }
            } catch (measurementErr) {
              console.error('MeasurementPrediction: Error:', measurementErr.message);
            }
          } else {
            console.log('Intelligence: No captureId available');
          }
        } else {
          console.log('Intelligence: Missing required data');
          console.log('  - photoBase64:', !!photoBase64);
          console.log('  - selectedConcept:', !!selectedConcept);
          console.log('  - selectedProject:', !!selectedProject);
        }
      } catch (error) {
        // Silent failure - never interrupt the residente's flow
        console.error('Intelligence: Error triggering analysis:', error.message);
      }
    } else {
      console.log('Intelligence: No currentShape, skipping');
    }
  };

  // Handle pin add for pza mode - store color with each pin
  const handlePinAdd = (pin) => {
    // Pin includes its own color based on current SUMA/DEDUCCIÓN mode
    setPins(prev => [...prev, { ...pin, color: isDeduction ? '#EF4444' : '#22C55E' }]);
  };

  // Handle UNDO for pza mode - removes last pin only
  const handlePinUndo = () => {
    if (pins.length > 0) {
      setPins(prev => {
        const newPins = prev.slice(0, -1);
        // Renumber remaining pins
        return newPins.map((p, idx) => ({ ...p, number: idx + 1 }));
      });
      toast.info('Último pin eliminado');
    }
  };

  // Handle adding figure for pza mode (pins)
  const handleAddPzaFigure = async (formData) => {
    if (pins.length === 0) {
      toast.error('Error: marca al menos un elemento en la foto');
      return;
    }
    
    if (!photoBase64) {
      toast.error('Error: carga una foto primero');
      return;
    }

    try {
      const cap = await createCaptureIfNeeded();
      const currentFigureNumber = figureCounter;

      // Count SUMA vs DEDUCCION pins
      const sumaPins = pins.filter(p => p.color === '#22C55E').length;
      const deduccionPins = pins.filter(p => p.color === '#EF4444').length;
      
      // Net quantity: SUMA - DEDUCCION
      const netQuantity = sumaPins - deduccionPins;
      const tipo = netQuantity >= 0 ? 'suma' : 'deduccion';

      // Convert pins to percentage coordinates for storage
      const pinsAsPercent = canvasRef.current?.getPinsAsPercent?.(pins) || pins;

      const figureData = {
        capture_id: cap.id,
        project_id: selectedProject.id,
        concept_id: selectedConcept.id,
        ubicacion: formData.ubicacion || '',
        tipo: tipo,
        shape_data: { type: 'pins', isPercent: true },
        pins: pinsAsPercent,
        piezas: pins.length,
        cantidad: Math.abs(netQuantity),
        destino: captureDestino || null,
      };

      const saved = await saveFigure(cap, figureData, currentFigureNumber);
      
      const completedFigure = {
        ...saved.data,
        piezas: pins.length,
        cantidad: Math.abs(netQuantity),
        ubicacion: formData.ubicacion || '',
      };
      
      setFigures(prev => [...prev, completedFigure]);
      setFigureCounter(prev => prev + 1);
      // DO NOT clear pins here - they stay visible until FOTO LISTA
      setIsDeduction(false);
      setIsDeductionLocked(false);
      
      // CRITICAL: Stay in PHOTO_LOADED to allow more figures - never force FOTO LISTA
      setCaptureState(CAPTURE_STATES.PHOTO_LOADED);
      
      toast.success(
        saved.local
          ? `Figura ${String(currentFigureNumber).padStart(6, '0')} guardada en el dispositivo (se sincronizará)`
          : `Figura ${String(currentFigureNumber).padStart(6, '0')} agregada con ${pins.length} pines`,
        { action: { label: 'Ver presupuesto', onClick: () => navigate('/presupuesto') } }
      );
    } catch (error) {
      console.error('Error adding pza figure:', error);
      toast.error('Error al agregar figura');
    }
  };

  // 4. Figure added from form -> Back to PHOTO_LOADED for next figure
  const handleAddFigure = async (formData) => {
    // Handle pza mode separately - use normalized unit
    if (normalizeUnit(selectedConcept?.unidad) === 'pza') {
      return handleAddPzaFigure(formData);
    }

    if (!currentShape) {
      toast.error('Error: selecciona una figura primero');
      return;
    }
    
    if (!photoBase64) {
      toast.error('Error: carga una foto primero');
      return;
    }

    try {
      const cap = await createCaptureIfNeeded();
      const currentCaptureId = cap.id;
      const currentFigureNumber = figureCounter;
      
      // Get label positions from canvas as percentages for storage
      const labelPositionsPercent = canvasRef.current?.getLabelPositionsAsPercent?.() || {};
      
      // Convert shape data to percentages for storage
      const shapeAsPercent = canvasRef.current?.getShapeAsPercent?.(currentShape) || currentShape;

      // Build shape_data based on shape type - using percentage coordinates
      let shapeData = {
        type: shapeAsPercent.type,
        isPercent: true, // Mark as percentage-based coordinates
      };
      
      if (shapeAsPercent.type === 'circle') {
        shapeData.center = shapeAsPercent.center;
        shapeData.radius = shapeAsPercent.radius;
      } else if (shapeAsPercent.type === 'line') {
        shapeData.points = shapeAsPercent.vertices;
      } else {
        shapeData.vertices = shapeAsPercent.vertices;
      }

      const figureData = {
        capture_id: currentCaptureId,
        project_id: selectedProject.id,
        concept_id: selectedConcept.id,
        ubicacion: formData.ubicacion || '',
        tipo: isDeduction ? 'deduccion' : 'suma',
        shape_data: shapeData,
        color: currentShape.color,
        largo: formData.largo ? parseFloat(formData.largo) : null,
        alto: formData.alto ? parseFloat(formData.alto) : null,
        profundidad: formData.profundidad ? parseFloat(formData.profundidad) : null,
        base: formData.base ? parseFloat(formData.base) : null,
        base_mayor: formData.baseMayor ? parseFloat(formData.baseMayor) : null,
        base_menor: formData.baseMenor ? parseFloat(formData.baseMenor) : null,
        altura: formData.altura ? parseFloat(formData.altura) : null,
        diametro: formData.diametro ? parseFloat(formData.diametro) : null,
        longitud: formData.longitud ? parseFloat(formData.longitud) : null,
        cantidad: formData.cantidad || 0,
        label_positions: labelPositionsPercent,
        intelligence_analysis_id: intelligenceAnalysisId || null,  // Link to Vision analysis
        destino: captureDestino || null,
      };

      // Log color being saved for verification
      console.log(`SAVING FIGURE: figureNumber=${currentFigureNumber}, tipo=${figureData.tipo}, color=${figureData.color}`);
      console.log(`Intelligence: Passing analysis_id=${intelligenceAnalysisId || 'null'} to figure save`);

      const saved = await saveFigure(cap, figureData, currentFigureNumber);
      
      // Save completed shape with measurements AND label positions (in percentage for consistency)
      const completedShape = {
        ...shapeAsPercent,
        id: currentShape.id, // Keep original ID
        color: currentShape.color,
        figureNumber: currentFigureNumber,
        measurements: formData,
        labelPositions: labelPositionsPercent, // Save label positions as percentages
        isDeduction: isDeduction,
      };
      
      setSavedShapes(prev => [...prev, completedShape]);
      setFigures(prev => [...prev, { ...saved.data, ...formData, cantidad: formData.cantidad }]);
      setFigureCounter(prev => prev + 1);
      setCurrentShape(null);
      setSelectedShapeType(null);
      setLiveMeasurements({});
      setIsDeduction(false);
      setIsDeductionLocked(false);
      setIntelligenceAnalysisId(null);  // Reset for next figure
      setCalibrationStatus(null);  // Reset calibration status for next figure
      
      // Confirmaciones de Vision solo cuando la figura y la captura están en el servidor
      const canConfirmIntelligence = !saved.local && !cap.isLocal && navigator.onLine;
      
      // Confirm figure prediction with full residente_figura data (silent)
      if (figurePredictionId && canConfirmIntelligence) {
        try {
          // Convert vertices to puntos format (0-1 range for consistency)
          // shapeAsPercent.vertices are in 0-100 range, convert to 0-1
          const puntos = (shapeAsPercent.vertices || []).map(v => ({
            x: v.x / 100,
            y: v.y / 100
          }));
          
          await postWithTimeout(`${API}/intelligence/confirm-figure/${figurePredictionId}`, {
            figura_type: shapeAsPercent.type || 'rectangle',
            puntos: puntos,
            color: currentShape.color,
            capture_id: currentCaptureId,
            photo_number: photoNumber
          });
          console.log('FigurePrediction: Confirmed with puntos:', puntos.length, 'points, capture:', currentCaptureId);
        } catch (err) {
          console.error('FigurePrediction: Error confirming:', err.message);
        }
      }
      setFigurePredictionId(null);  // Reset for next photo
      
      // Confirm Measurement Prediction with residente values (silent)
      if (currentCaptureId && canConfirmIntelligence) {
        try {
          await postWithTimeout(`${API}/intelligence/confirm-measurements/${currentCaptureId}`, {
            largo_m: formData.largo ? parseFloat(formData.largo) : null,
            alto_m: formData.alto ? parseFloat(formData.alto) : null,
            capture_id: currentCaptureId
          });
          console.log('MeasurementPrediction: Confirmed with largo:', formData.largo, 'alto:', formData.alto);
        } catch (err) {
          console.error('MeasurementPrediction: Error confirming:', err.message);
        }
      }
      setMeasurementPredictionId(null);  // Reset for next figure
      
      // Store label positions with figureId prefix BEFORE clearing
      // This preserves them in labelStore for the saved figure
      canvasRef.current?.storeLabelPositionsForFigure?.(completedShape.id, labelPositionsPercent);
      
      // Clear label positions for next figure (only clears keys without figureId prefix)
      canvasRef.current?.clearLabelPositions?.();
      
      // Go back to PHOTO_LOADED to allow adding more figures
      setCaptureState(CAPTURE_STATES.PHOTO_LOADED);
      toast.success(
        saved.local
          ? `Figura ${String(currentFigureNumber).padStart(6, '0')} guardada en el dispositivo (se sincronizará)`
          : `Figura ${String(currentFigureNumber).padStart(6, '0')} agregada`,
        { action: { label: 'Ver presupuesto', onClick: () => navigate('/presupuesto') } }
      );
    } catch (error) {
      console.error('Error adding figure:', error);
      toast.error('Error al agregar figura');
    }
  };

  // UNDO - Remove last shape OR last pin (also unlocks SUMA/DEDUCCIÓN)
  const handleUndo = () => {
    // For pza mode, only remove last pin (not renumber)
    if (normalizeUnit(selectedConcept?.unidad) === 'pza' && pins.length > 0) {
      setPins(prev => {
        const newPins = prev.slice(0, -1);
        // Renumber remaining pins sequentially
        return newPins.map((p, idx) => ({ ...p, number: idx + 1 }));
      });
      toast.info('Último pin eliminado');
      return;
    }
    
    if (currentShape) {
      setCurrentShape(null);
      setSelectedShapeType(null);
      setIsDeductionLocked(false); // Unlock toggle
      setCaptureState(CAPTURE_STATES.PHOTO_LOADED);
      toast.info('Figura descartada');
    } else if (savedShapes.length > 0) {
      const lastShape = savedShapes[savedShapes.length - 1];
      // Si la figura estaba guardada solo en el dispositivo, quitarla de la cola de sincronización
      const localFig = figures.find(f => f.figure_number === lastShape.figureNumber && f.local);
      if (localFig) {
        offlineDb.deleteFigure(localFig.id).then(() => syncManager.refreshPending()).catch(() => null);
      }
      setSavedShapes(prev => prev.slice(0, -1));
      setFigures(prev => prev.filter(f => f.figure_number !== lastShape.figureNumber));
      setFigureCounter(prev => prev - 1);
      toast.info('Última figura eliminada');
    }
  };

  // Photo complete -> Show navigation options (NOT navigate away)
  const handlePhotoComplete = () => {
    toast.success('Foto lista');
    setCaptureState(CAPTURE_STATES.PHOTO_COMPLETE);
  };

  // New Photo - clear and stay in capture
  // Note: figureCounter is NOT reset - continues from last project number
  const handleNewPhoto = async () => {
    setPhotoBase64(null);
    setCaptureId(null);
    captureRef.current = null;
    capturePromiseRef.current = null;
    setCurrentShape(null);
    setSavedShapes([]);
    setFigures([]);
    setSelectedShapeType(null);
    setLiveMeasurements({});
    setIsDeduction(false);
    setIsDeductionLocked(false);
    setPins([]);
    setCaptureState(CAPTURE_STATES.NO_PHOTO);
    
    // Reset canvas ref
    if (canvasRef.current?.reset) {
      canvasRef.current.reset();
    }
    
    // Load next photo number for new photo (funciona sin conexión)
    loadNextPhotoNumber();
  };

  // Change Concept - go to catálogo
  const handleChangeConcept = () => {
    setSelectedConcept(null);
    handleNewPhoto();
    navigate('/catalogo');
  };

  // Update live measurements for dimension labels
  const handleMeasurementsChange = useCallback((measurements) => {
    setLiveMeasurements(measurements);
  }, []);

  // Redirect if no project/concept selected
  useEffect(() => {
    if (!selectedProject) {
      navigate('/contratos');
    } else if (!selectedConcept) {
      navigate('/catalogo');
    }
  }, [selectedProject, selectedConcept, navigate]);

  // Check if project/concept selected (show loading while redirecting)
  if (!selectedProject || !selectedConcept) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#1B3A6B] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Redirigiendo...</p>
        </div>
      </div>
    );
  }

  // Portrait orientation message - OVERLAY without unmounting component
  // Photo stays in memory while overlay is shown
  const portraitOverlay = !isLandscape && (
    <div style={{
      position: 'fixed',
      top: 0, left: 0,
      width: '100%', height: '100%',
      background: 'rgba(0,0,0,0.9)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'column'
    }}>
      <RotateCcw className="w-24 h-24 text-white mb-6 animate-pulse" />
      <p style={{color:'white', fontSize:'20px', textAlign:'center'}}>
        🔄 Gira el iPad para continuar
      </p>
      <p style={{color:'#94a3b8', fontSize:'14px', marginTop:'8px'}}>
        La pantalla de captura funciona en modo horizontal
      </p>
    </div>
  );

  return (
    <div className="flex flex-col h-screen overflow-hidden" data-testid="capture-page">
      {/* Portrait overlay - component stays mounted below */}
      {portraitOverlay}
      
      <TopBar photoNumber={photoBase64 ? photoNumber : null} />

      <div className="capture-split flex-1">
        {/* Left Panel: Photo + Canvas ONLY - Clean area */}
        <div className="canvas-panel" data-testid="canvas-panel">
          <ConstructionCanvas
            ref={canvasRef}
            captureState={captureState}
            onPhotoUpload={handlePhotoUpload}
            onShapeCreated={setCurrentShape}
            currentShape={currentShape}
            savedShapes={savedShapes}
            isDeduction={isDeduction}
            figureNumber={figureCounter}
            selectedShapeType={selectedShapeType}
            liveMeasurements={liveMeasurements}
            unit={selectedConcept?.unidad}
            pins={pins}
            onPinAdd={handlePinAdd}
          />
        </div>

        {/* Right Panel: ALL Controls */}
        <div className="form-panel" data-testid="form-panel">
          <MeasurementForm
            unit={selectedConcept.unidad}
            figureNumber={figureCounter}
            isDeduction={isDeduction}
            setIsDeduction={setIsDeduction}
            isDeductionLocked={isDeductionLocked}
            captureState={captureState}
            CAPTURE_STATES={CAPTURE_STATES}
            selectedShapeType={selectedShapeType}
            onShapeSelect={handleShapeSelect}
            onConfirmShape={handleConfirmShape}
            onAddFigure={handleAddFigure}
            onUndo={handleUndo}
            onMeasurementsChange={handleMeasurementsChange}
            onPhotoComplete={handlePhotoComplete}
            onNewPhoto={handleNewPhoto}
            onChangeConcept={handleChangeConcept}
            figures={figures}
            pinCount={pins.length}
            hasPhoto={!!photoBase64}
            canUndo={!!currentShape || savedShapes.length > 0 || pins.length > 0}
            calibrationStatus={calibrationStatus}
          />
        </div>
      </div>
    </div>
  );
};

export default CapturePage;
